import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { IonRange } from "@ionic/react";
import IonicButton from "./Button";
/**
 * ImageCropperDialog
 *
 * Modal dialog for cropping one or more uploaded images before upload.
 *
 * Props
 * -----
 * @prop {File[]} images
 *   Array of image Files selected by the user.
 *
 * @prop {number} [aspectRatio=1]
 *   Optional crop aspect ratio (e.g. 1, 16/9, 4/3).
 *
 * @prop {(cropped: File[]) => void} onFinished
 *   Called when all images are cropped. Receives new File[].
 *
 * @prop {() => void} [onCancel]
 *   Called when the user cancels the dialog.
 *
 * @example Usage Example:
 * ```tsx
 * <ImageCropperDialog
 *   images={files}
 *   aspectRatio={1}
 *   onFinished={(cropped) => uploadToServer(cropped)}
 *   onCancel={() => setShowCropper(false)}
 * />
 * ```
 */ export function ImageCropper({ images, onFinished, onCancel, aspectRatio = 1 }) {
    const [current, setCurrent] = useState(0); // index of active image
    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({
        x: 0,
        y: 0
    });
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedFiles, setCroppedFiles] = useState(Array(images.length).fill(null));
    const [thumbnailUrls, setThumbnailUrls] = useState([]);
    // const urls = useMemo(() => images.map(img => URL.createObjectURL(img)), [images]);
    const cropperUrl = thumbnailUrls?.[current];
    useEffect(()=>{
        const urls = images.map((file)=>URL.createObjectURL(file));
        setThumbnailUrls(urls);
        return ()=>{
            urls.forEach((url)=>URL.revokeObjectURL(url));
        };
    }, [
        images
    ]);
    const onCropComplete = useCallback((_area, areaPixels)=>{
        setCroppedAreaPixels(areaPixels);
    }, []);
    async function createCroppedFile(src, pixels, filename) {
        const image = await createImage(src);
        const canvas = document.createElement('canvas');
        canvas.width = pixels.width;
        canvas.height = pixels.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, pixels.x, pixels.y, pixels.width, pixels.height, 0, 0, pixels.width, pixels.height);
        return new Promise((resolve)=>{
            canvas.toBlob((blob)=>{
                resolve(new File([
                    blob
                ], filename, {
                    type: 'image/jpeg'
                }));
            }, 'image/jpeg');
        });
    }
    const handleConfirm = async ()=>{
        if (!croppedAreaPixels) return;
        const file = images[current];
        const url = URL.createObjectURL(file);
        const cropped = await createCroppedFile(url, croppedAreaPixels, file.name);
        const next = [
            ...croppedFiles
        ];
        next[current] = cropped;
        setCroppedFiles(next);
        // Move to next image or finish
        if (current < images.length - 1) {
            setCurrent((c)=>c + 1);
        } else {
            // All done
            onFinished(next.filter((f)=>!!f));
        }
    };
    // useEffect(() => {
    //   return () => {
    //     URL.revokeObjectURL(cropperUrl);
    //   };
    // }, [cropperUrl]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "gap-4 w-full flex flex-col",
        children: [
            thumbnailUrls?.length > 1 && /*#__PURE__*/ _jsx("div", {
                className: "flex gap-2 overflow-x-auto",
                children: thumbnailUrls?.map((file, idx)=>{
                    return /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>setCurrent(idx),
                        className: `w-14 h-14 rounded-sm overflow-hidden border-2 relative
              ${current === idx ? 'border-blue-500' : 'border-gray-300'}
              ${croppedFiles[idx] ? 'border-green-400 after:absolute after:top-0 after:left-0 after:z-10 after:w-full after:h-full  rounded-sm bg-green-400/50 ' : ''}`,
                        children: /*#__PURE__*/ _jsx("img", {
                            src: file,
                            alt: "",
                            className: "object-cover w-full h-full"
                        })
                    }, idx);
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative  w-full h-[calc(55vh-5.5rem)]",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "absolute top-0 left-0 bottom-0 right-0",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "absolute top-0 left-0 bottom-0 right-0",
                        children: /*#__PURE__*/ _jsx(Cropper, {
                            image: cropperUrl,
                            crop: crop,
                            zoom: zoom,
                            aspect: aspectRatio,
                            onCropChange: setCrop,
                            onZoomChange: setZoom,
                            onCropComplete: onCropComplete,
                            style: {
                                containerStyle: {
                                    background: 'rgba(0,0,0,0.75)'
                                },
                                cropAreaStyle: {
                                    border: '2px solid #fff'
                                }
                            }
                        })
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3 sm:w-2/3 mx-auto",
                children: [
                    /*#__PURE__*/ _jsx(IonRange, {
                        style: {
                            '--bar-height': '4px',
                            '--bar-border-radius': '8px',
                            '--knob-size': '30px'
                        },
                        min: 1,
                        max: 3,
                        step: 0.1,
                        snaps: false,
                        value: zoom,
                        onIonChange: (e)=>setZoom(e.detail.value)
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 flex-wrap",
                        children: [
                            /*#__PURE__*/ _jsx(IonicButton, {
                                variant: "outline",
                                className: "flex-1 min-w-fit block",
                                onClick: onCancel,
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(IonicButton, {
                                className: "flex-1 min-w-fit block",
                                onClick: handleConfirm,
                                children: current < images.length - 1 ? 'Confirm Crop' : 'Finish'
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
/* --- Utility to create an HTMLImageElement --- */ function createImage(url) {
    return new Promise((resolve, reject)=>{
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = ()=>resolve(img);
        img.onerror = (e)=>reject(e);
        img.src = url;
    });
}

//# sourceMappingURL=ImageCropper.js.map